import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { notFoundSeo, seoForPath, socialImage, structuredData } from '../../shared/seo';
import { InitialDataContext } from '../initialData';

/** Finds `<tag>` by its identifying attributes (creating it if absent) and sets `attributes`. */
function upsertHeadTag(tag: 'meta' | 'link', identity: Record<string, string>, attributes: Record<string, string>) {
  const selector = tag + Object.entries(identity).map(([key, value]) => `[${key}="${value}"]`).join('');
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement(tag);
    for (const [key, value] of Object.entries(identity)) element.setAttribute(key, value);
    document.head.append(element);
  }
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
}

/**
 * Keeps title/description/canonical/Open Graph in step with SPA navigation. The prerendered HTML
 * already carries the same values for the first load; this only matters after client-side routing.
 */
export function RouteHead() {
  const { pathname } = useLocation();
  const facts = useContext(InitialDataContext).excursions;
  useEffect(() => {
    const route = seoForPath(pathname) ?? notFoundSeo;
    const url = `${window.location.origin}${route.path}`;
    document.title = route.title;
    upsertHeadTag('meta', { name: 'description' }, { content: route.description });
    upsertHeadTag('meta', { name: 'robots' }, { content: route.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large' });
    upsertHeadTag('meta', { property: 'og:title' }, { content: route.title });
    upsertHeadTag('meta', { property: 'og:description' }, { content: route.description });
    upsertHeadTag('meta', { property: 'og:url' }, { content: url });
    upsertHeadTag('meta', { property: 'og:image' }, { content: `${window.location.origin}${socialImage(route).path}` });
    upsertHeadTag('link', { rel: 'canonical' }, { href: url });
    let jsonLd = document.head.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      document.head.append(jsonLd);
    }
    jsonLd.textContent = JSON.stringify(structuredData(route, window.location.origin, facts));
  }, [pathname, facts]);
  return null;
}
